import { memo, useMemo } from 'react';
import ReactMarkdown, { defaultUrlTransform, type Options } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

import { MarkdownRendererProps } from './MarkdownRenderer.types';
import { useMarkdownStreamAnimation } from './markdownStreamAnimation.utils';
import { rehypeSourceLines } from './markdownSourceLines.utils';
import { MarkdownAnchorRenderer } from '../MarkdownAnchorRenderer';
import { MarkdownRendererContextProvider } from '../MarkdownRendererContextProvider';
import {
  MarkdownEmRenderer,
  MarkdownLiRenderer,
  MarkdownParagraphRenderer,
  MarkdownSpanRenderer,
  MarkdownStrongRenderer,
} from '../MarkdownComponentsWithHighlights';

const MarkdownRendererBase: React.FC<MarkdownRendererProps> = ({
  content,
  components,
  highlights,
  matchWholeWords,
  className,
  singleTildeStrikethrough,
  preserveSoftLineBreaks = false,
  allowRawHtml = false,
  isStreaming = false,
  allowedUrlProtocols,
  sourceLineAttributes = false,
  rehypePlugins: extraRehypePlugins,
}) => {
  const streamAnimationPlugin = useMarkdownStreamAnimation(isStreaming);

  const urlTransform = useMemo((): Options['urlTransform'] => {
    if (!allowedUrlProtocols?.length) return undefined;

    const prefixes = allowedUrlProtocols.map((protocol) => `${protocol.toLowerCase()}:`);

    return (url) => (prefixes.some((prefix) => url.toLowerCase().startsWith(prefix)) ? url : defaultUrlTransform(url));
  }, [allowedUrlProtocols]);

  const remarkPlugins = useMemo((): Options['remarkPlugins'] => {
    const plugins: NonNullable<Options['remarkPlugins']> = [[remarkGfm, { singleTilde: singleTildeStrikethrough }]];
    if (preserveSoftLineBreaks) {
      plugins.push(remarkBreaks);
    }
    return plugins;
  }, [preserveSoftLineBreaks, singleTildeStrikethrough]);

  const rehypePlugins = useMemo((): Options['rehypePlugins'] => {
    const plugins: NonNullable<Options['rehypePlugins']> = [];

    if (allowRawHtml) {
      plugins.push(rehypeRaw);
    }

    if (streamAnimationPlugin) {
      plugins.push(streamAnimationPlugin);
    }

    if (sourceLineAttributes) {
      plugins.push(rehypeSourceLines);
    }

    if (extraRehypePlugins) {
      plugins.push(...extraRehypePlugins);
    }

    return plugins.length ? plugins : undefined;
  }, [allowRawHtml, streamAnimationPlugin, sourceLineAttributes, extraRehypePlugins]);

  return (
    <MarkdownRendererContextProvider highlights={highlights} matchWholeWords={matchWholeWords}>
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        urlTransform={urlTransform}
        components={{
          a: MarkdownAnchorRenderer,
          p: MarkdownParagraphRenderer,
          strong: MarkdownStrongRenderer,
          em: MarkdownEmRenderer,
          li: MarkdownLiRenderer,
          span: MarkdownSpanRenderer,
          ...components,
        }}
        className={className}
      >
        {content}
      </ReactMarkdown>
    </MarkdownRendererContextProvider>
  );
};

export const MarkdownRenderer = memo(MarkdownRendererBase);
